import React from 'react';
import { Box, Skeleton } from '@mui/material';

const ROW_LAYOUTS = [
  { ticker: 82, company: 176, sector: 96, phaseTitle: 118, meta: 164, confidence: 48, date: 84, flags: 72 },
  { ticker: 74, company: 162, sector: 88, phaseTitle: 104, meta: 152, confidence: 40, date: 92, flags: 58 },
  { ticker: 90, company: 188, sector: 104, phaseTitle: 126, meta: 170, confidence: 44, date: 88, flags: 64 },
  { ticker: 68, company: 154, sector: 92, phaseTitle: 96, meta: 144, confidence: 36, date: 80, flags: 54 },
  { ticker: 78, company: 168, sector: 112, phaseTitle: 112, meta: 158, confidence: 42, date: 86, flags: 68 },
  { ticker: 84, company: 174, sector: 108, phaseTitle: 108, meta: 149, confidence: 46, date: 90, flags: 61 },
];

function getTableTemplate({ showCompany = true, showSector = true, showScores = true }) {
  const columns = [
    { key: 'rank', width: '52px', header: 18 },
    { key: 'ticker', width: showCompany ? '102px' : '140px', header: showCompany ? '68%' : '72%' },
  ];

  if (showCompany) {
    columns.push({ key: 'company', width: '252px', header: '72%' });
  }
  if (showSector) {
    columns.push({ key: 'sector', width: '142px', header: '62%' });
  }

  columns.push(
    { key: 'phase', width: showCompany ? '196px' : '246px', header: '58%' },
    { key: 'confidence', width: '142px', header: '76%' },
  );

  if (showScores) {
    columns.push({ key: 'scores', width: '118px', header: '82%' });
  }

  columns.push(
    { key: 'flags', width: '112px', header: '64%' },
    { key: 'date', width: '108px', header: '56%' },
  );

  return columns;
}

function SummaryCardsSkeleton() {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 1.5, mb: 3 }}>
      {[1, 2, 3, 4].map((item) => (
        <Box
          key={item}
          sx={{
            background: '#fff',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
            px: 1.6,
            py: 1.35,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
              <Skeleton variant="rounded" width={34} height={34} sx={{ borderRadius: '8px', flexShrink: 0 }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Skeleton variant="text" width="72%" height={20} />
                <Skeleton variant="text" width="52%" height={14} sx={{ mt: 0.2 }} />
              </Box>
            </Box>
            <Box sx={{ textAlign: 'right', flexShrink: 0, minWidth: 52 }}>
              <Skeleton variant="text" width={42} height={34} sx={{ ml: 'auto' }} />
              <Skeleton variant="text" width={30} height={16} sx={{ mt: 0.2, ml: 'auto' }} />
            </Box>
          </Box>

          <Box sx={{ mt: 1.15 }}>
            <Skeleton variant="rounded" height={4} sx={{ borderRadius: 999 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.75 }}>
              <Skeleton variant="text" width={64} height={14} />
              <Skeleton variant="text" width={52} height={14} />
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function HeaderSkeleton() {
  return (
    <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Skeleton variant="rounded" width={44} height={44} sx={{ borderRadius: '12px' }} />
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Skeleton variant="text" width={180} height={28} />
            <Skeleton variant="rounded" width={82} height={24} sx={{ borderRadius: '6px' }} />
          </Box>
          <Skeleton variant="text" width={220} height={18} sx={{ mt: 0.15 }} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Skeleton variant="rounded" width={160} height={40} sx={{ borderRadius: '4px' }} />
        <Skeleton variant="rounded" width={106} height={36} sx={{ borderRadius: '4px' }} />
      </Box>
    </Box>
  );
}

function PreMarkupSkeleton() {
  return (
    <Box sx={{ mt: 3, mb: 3, background: '#fff', borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
      <Skeleton variant="rectangular" height={3} />
      <Box sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 1.7, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: '10px' }} />
            <Box>
              <Skeleton variant="text" width={190} height={24} />
              <Skeleton variant="text" width={280} height={16} />
            </Box>
          </Box>
          <Skeleton variant="rounded" width={104} height={26} sx={{ borderRadius: '999px' }} />
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: '290px 132px 110px 1fr 74px', gap: 1.6, px: 2.2, pb: 0.8 }}>
          {[72, 44, 58, 84, 34].map((width, idx) => (
            <Skeleton key={idx} variant="text" width={width} height={16} />
          ))}
        </Box>
        {[1, 2, 3].map((item) => (
          <Box
            key={item}
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'center' },
              gap: { xs: 1.2, md: 1.6 },
              px: 2.2,
              py: 1.35,
              borderBottom: item < 3 ? '1px solid #f1f5f9' : 'none',
              flexWrap: { xs: 'wrap', md: 'nowrap' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, width: { xs: '100%', md: 290 }, minWidth: 0, flexShrink: 0 }}>
              <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: '8px' }} />
              <Box>
                <Skeleton variant="text" width={56} height={18} />
                <Skeleton variant="text" width={48} height={14} />
                <Skeleton variant="text" width={164} height={16} />
              </Box>
            </Box>
            <Box sx={{ width: { xs: '100%', md: 132 }, flexShrink: 0 }}>
              <Skeleton variant="text" width={86} height={18} />
            </Box>
            <Box sx={{ width: 110, flexShrink: 0 }}>
              <Skeleton variant="text" width={44} height={18} />
              <Skeleton variant="rounded" height={5} sx={{ borderRadius: 3 }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 0.8, flex: 1, flexWrap: 'wrap' }}>
              {[88, 96, 84, 92].map((width, chip) => (
                <Skeleton key={chip} variant="rounded" width={width} height={24} sx={{ borderRadius: '7px' }} />
              ))}
            </Box>
            <Skeleton variant="rounded" width={74} height={24} sx={{ borderRadius: '7px' }} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function ToolbarSkeleton() {
  return (
    <Box sx={{ mt: 3, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: { xs: 1.5, sm: 1 } }}>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {[108, 96, 122, 112].map((width) => (
          <Skeleton key={width} variant="rounded" width={width} height={40} sx={{ borderRadius: '10px' }} />
        ))}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: '10px' }} />
        <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: '10px' }} />
        <Skeleton variant="rounded" width={60} height={32} sx={{ borderRadius: '8px' }} />
      </Box>
    </Box>
  );
}

function SectionHeaderSkeleton({ titleWidth = 86, countWidth = 56 }) {
  return (
    <Box sx={{ mb: 1.25, px: 0.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
      <Skeleton variant="text" width={titleWidth} height={22} />
      <Skeleton variant="text" width={countWidth} height={16} />
    </Box>
  );
}

function TableSkeletonGrid({ rows = 6, columns, showCompany = true, showSector = true, showScores = true }) {
  const templateColumns = columns.map((column) => column.width).join(' ');

  return (
    <Box sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', background: '#fff', overflow: 'hidden', boxShadow: '0 4px 18px rgba(15, 23, 42, 0.05)' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: templateColumns, gap: 2, px: 3, py: 1.15, borderBottom: '1px solid #e2e8f0', backgroundColor: '#fafcff' }}>
        {columns.map((column, index) => (
          <Skeleton key={`${column.key}-${index}`} variant="text" width={column.header} height={18} />
        ))}
      </Box>

      {ROW_LAYOUTS.slice(0, rows).map((layout, index) => (
        <Box key={`${layout.ticker}-${index}`} sx={{ display: 'grid', gridTemplateColumns: templateColumns, gap: 2, alignItems: 'center', px: 3, py: 1.25, borderBottom: index < rows - 1 ? '1px solid #eef2f7' : 'none' }}>
          <Skeleton variant="rounded" width={44} height={44} sx={{ borderRadius: '14px' }} />

          <Box>
            <Skeleton variant="text" width={layout.ticker} height={22} />
          </Box>

          {showCompany ? (
            <Box>
              <Skeleton variant="text" width={layout.company} height={18} />
              <Skeleton variant="text" width={Math.max(layout.company - 24, 96)} height={18} />
            </Box>
          ) : null}

          {showSector ? <Skeleton variant="text" width={layout.sector} height={18} /> : null}

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.45 }}>
              <Skeleton variant="circular" width={12} height={12} />
              <Skeleton variant="text" width={layout.phaseTitle} height={22} />
            </Box>
            <Skeleton variant="text" width={layout.meta} height={16} />
          </Box>

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, mb: 0.35 }}>
              <Skeleton variant="rounded" width={118} height={8} sx={{ borderRadius: 999 }} />
              <Skeleton variant="text" width={layout.confidence} height={22} />
            </Box>
            <Skeleton variant="text" width={66} height={16} />
          </Box>

          {showScores ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Skeleton variant="text" width={40} height={24} />
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: 22 }}>
                {[14, 20, 8, 17].map((height, barIndex) => (
                  <Skeleton key={`${height}-${barIndex}`} variant="rounded" width={8} height={height} sx={{ borderRadius: '2px' }} />
                ))}
              </Box>
            </Box>
          ) : null}

          <Box>
            <Skeleton variant="text" width={layout.flags} height={20} />
            <Skeleton variant="text" width={Math.max(layout.flags - 10, 44)} height={14} />
          </Box>

          <Box>
            <Skeleton variant="text" width={layout.date} height={20} />
            <Skeleton variant="text" width={70} height={14} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export default function PhaseSkeleton({ variant = 'page', rows = 6, withToolbar = false }) {
  if (variant === 'table') {
    const columns = getTableTemplate({ showCompany: false, showSector: false, showScores: true });
    return <TableSkeletonGrid rows={rows} columns={columns} showScores />;
  }

  if (variant === 'section') {
    const columns = getTableTemplate({ showCompany: true, showSector: true, showScores: true });
    return (
      <Box>
        <SectionHeaderSkeleton />
        {withToolbar ? <ToolbarSkeleton /> : null}
        <TableSkeletonGrid rows={rows} columns={columns} showCompany showSector showScores />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
      <HeaderSkeleton />
      <SummaryCardsSkeleton />
      <PreMarkupSkeleton />

      <Box sx={{ mt: 3, mb: 3 }}>
        <SectionHeaderSkeleton titleWidth={72} countWidth={62} />
        <TableSkeletonGrid
          rows={3}
          columns={getTableTemplate({ showCompany: false, showSector: false, showScores: true })}
          showScores
        />
      </Box>

      <ToolbarSkeleton />

      <Box>
        <SectionHeaderSkeleton titleWidth={84} countWidth={74} />
        <TableSkeletonGrid
          rows={6}
          columns={getTableTemplate({ showCompany: true, showSector: true, showScores: true })}
          showCompany
          showSector
          showScores
        />
      </Box>
    </Box>
  );
}
